import { useCallback, useEffect, useRef } from "react";

import type { EmitterSource } from "quill";
import Quill from "quill";
import Delta from "quill-delta";
import { useDispatch } from "react-redux";

import { showSuccessToast } from "app/actions/toast_actions";
import { translate } from "app/filters/i18nFilter";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import type { Contact } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import {
    buildEmailHtml,
    getCurrentImage,
    getImageIndex,
    stripTrailingNewline,
} from "app/pages/sales-intelligence/modules/ai-agent/utils/quillEditor";

type UseQuillEditorProps = {
    value?: string;
    onTextChange: (value: string, imageIndex: number) => void;
    imgSrc?: string | null;
    imageIndex?: number | null;
    imageClassName?: string | undefined;
    companyName?: string;
};

export const useQuillEditor = ({
    value,
    onTextChange,
    imgSrc,
    imageIndex,
    imageClassName,
    companyName = "",
}: UseQuillEditorProps) => {
    const editorElRef = useRef<HTMLDivElement | null>(null);
    const quillRef = useRef<Quill | null>(null);
    const dispatch = useDispatch();
    const imageIndexRef = useRef<number | null>(null);
    const prevClassRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        imageIndexRef.current =
            Number.isFinite(imageIndex as number) && (imageIndex as number) >= 0 ? (imageIndex as number) : null;
    }, [imageIndex]);

    useEffect(() => {
        const el = editorElRef.current;
        if (!el || quillRef.current) {
            return;
        }

        const quill = new Quill(el, {
            modules: { toolbar: false },
        });
        quillRef.current = quill;

        const startText: string = value ?? "";
        if (startText) {
            quill.setText(startText);
        }

        const handleTextChange = (_delta: unknown, _oldDelta: unknown, source: EmitterSource) => {
            if (source !== "user") {
                return;
            }
            AIAgentTrackingService.email.trackEmailBodyEdit();
            const v = stripTrailingNewline(quill.getText());
            onTextChange?.(v, getImageIndex(quill));
        };

        const handleManualCopy = () => {
            AIAgentTrackingService.email.trackManualCopyText(companyName);
        };

        const root = quill.root;
        root.addEventListener("copy", handleManualCopy);

        quill.on("text-change", handleTextChange);

        return () => {
            root?.removeEventListener("copy", handleManualCopy);
            quill.off("text-change", handleTextChange);
            quillRef.current = null;
            if (el) {
                el.innerHTML = "";
            }
        };
    }, []);

    useEffect(() => {
        const quill = quillRef.current;
        if (!quill || typeof value !== "string") {
            return;
        }

        const current = stripTrailingNewline(quill.getText());
        if (current !== value) {
            quill.setText(value, "silent");
        }
    }, [value]);

    useEffect(() => {
        const quill = quillRef.current;
        if (!quill) {
            return;
        }

        const src = imgSrc ?? null;
        const { idx: curIdx, src: curSrc } = getCurrentImage(quill);

        if (!src) {
            if (curIdx !== -1) {
                quill.updateContents(new Delta([{ retain: curIdx }, { delete: 1 }]), "silent");
            }
        } else if (curIdx === -1) {
            const len = quill.getLength?.() ?? 0;
            const hint = imageIndexRef.current;
            const target = Number.isFinite(hint as number) ? Math.max(0, Math.min(hint as number, len)) : len;
            quill.updateContents(new Delta([{ retain: target }, { insert: { image: src } }]), "silent");
        } else if (curSrc !== src) {
            quill.updateContents(new Delta([{ retain: curIdx }, { delete: 1 }, { insert: { image: src } }]), "silent");
        }

        requestAnimationFrame(() => {
            const idxNow = getImageIndex(quill);
            if (idxNow < 0) {
                return;
            }
            try {
                const leafTuple = quill.getLeaf(idxNow) as unknown as [{ domNode?: unknown }, number];
                const leaf = leafTuple?.[0];
                const node = (leaf && (leaf as { domNode?: unknown }).domNode) as HTMLElement | undefined;
                if (!node || node.tagName !== "IMG") {
                    return;
                }

                const prev = prevClassRef.current;
                if (prev && prev !== imageClassName) {
                    node.classList.remove(prev);
                }
                if (!imageClassName && prev) {
                    node.classList.remove(prev);
                }
                if (imageClassName) {
                    node.classList.add(imageClassName);
                }
                prevClassRef.current = imageClassName;
            } catch (err: unknown) {
                if (typeof process !== "undefined" && process.env.NODE_ENV !== "production") {
                    console.warn("[useQuillEditor] Failed to add image class", { err, imageClassName });
                }
            }
        });
    }, [imgSrc, imageClassName]);

    const onCopy = useCallback(
        async ({ subject, recipient }: { subject: string; recipient: Contact | null }) => {
            const quill = quillRef.current;
            if (!quill) {
                return;
            }
            if (!("ClipboardItem" in window) || !navigator.clipboard?.write) {
                return;
            }

            const email = recipient?.email || "";
            const subjectLine = subject || "";

            // Plain text
            const plainBody = stripTrailingNewline(quill.getText());
            const plain =
                `${email ? `${email}\n\n` : ""}` + `${subjectLine ? `Subject: ${subjectLine}\n` : ""}\n` + plainBody;

            // HTML — clone the editor DOM and hard-size images
            const sizedHtmlBody = buildEmailHtml(quill.root, 600);

            let htmlHeader = "";
            if (email) {
                htmlHeader += `<div>${email}</div><br>`;
            }
            if (subjectLine) {
                htmlHeader += `<div>Subject: ${subjectLine}</div>`;
            }
            htmlHeader += "<br>";
            const html = `${htmlHeader}${sizedHtmlBody}`;

            const item = new ClipboardItem({
                ["text/html"]: new Blob([html], { type: "text/html" }),
                ["text/plain"]: new Blob([plain], { type: "text/plain" }),
            });
            await navigator.clipboard.write([item]);
            dispatch(showSuccessToast(translate("si.pages.ai_agent_email.copied_to_clipboard")));
            AIAgentTrackingService.email.trackCopyText(companyName);
        },
        [companyName, dispatch],
    );

    return { editorElRef, onCopy } as const;
};
